import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Target, Activity, ShieldAlert, ExternalLink, RefreshCw } from "lucide-react";
import { fmtMDTTime } from "../lib/time";

const SEV_COLORS = {
  critical: "text-rose-600 dark:text-rose-400",
  high: "text-orange-600 dark:text-orange-400",
  medium: "text-amber-600 dark:text-amber-400",
  low: "text-sky-600 dark:text-sky-400",
  info: "text-muted-foreground",
};

export default function IntruderSummaryCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/intruder/summary");
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load Intruder summary");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalIssues = data?.issues
    ? (data.issues.critical || 0) + (data.issues.high || 0) + (data.issues.medium || 0) + (data.issues.low || 0)
    : 0;

  return (
    <div
      data-testid="intruder-summary-card"
      className="rounded-xl border border-border bg-card overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <ShieldAlert className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="text-[14px] font-semibold">Intruder Vulnerability Scan</h3>
            <p className="text-[11.5px] text-muted-foreground">
              Live counts from Intruder API
              {data?.updated_at && (
                <span> · updated {fmtMDTTime(data.updated_at)}</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={load}
            title="Refresh"
            className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <a
            data-testid="intruder-launch-link"
            href={data?.portal_url || "https://portal.intruder.io/"}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1 text-[12px] hover:border-primary/40"
          >
            Open portal <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {loading && !data ? (
        <div className="p-4 space-y-2">
          <div className="h-6 rounded bg-muted/50 animate-pulse w-1/2" />
          <div className="h-16 rounded bg-muted/40 animate-pulse" />
        </div>
      ) : err ? (
        <div className="p-4 text-[12.5px] text-destructive">{err}</div>
      ) : !data?.configured ? (
        <div className="p-4 text-[12.5px] text-muted-foreground">
          Intruder API key not configured. Set <code>INTRUDER_API_KEY</code> in backend/.env.
        </div>
      ) : (
        <div className="p-4 grid grid-cols-3 gap-3">
          <div className="rounded-lg border border-border p-3">
            <div className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
              <Target className="w-3.5 h-3.5" /> Monitored targets
            </div>
            <div data-testid="intruder-targets" className="mt-1 text-[22px] font-semibold tracking-tight">{data.targets}</div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
              <Activity className="w-3.5 h-3.5" /> Active scans
            </div>
            <div data-testid="intruder-active-scans" className="mt-1 text-[22px] font-semibold tracking-tight">{data.active_scans}</div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
              <ShieldAlert className="w-3.5 h-3.5" /> Open issues
            </div>
            <div data-testid="intruder-total-issues" className="mt-1 text-[22px] font-semibold tracking-tight">{totalIssues}</div>
          </div>
          <div className="col-span-3 flex flex-wrap gap-2 pt-1">
            {["critical", "high", "medium", "low"].map((sev) => (
              <span
                key={sev}
                data-testid={`intruder-issues-${sev}`}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/30 px-2.5 py-1 text-[11.5px]"
              >
                <span className="uppercase tracking-wide font-semibold text-muted-foreground">{sev}</span>
                <span className={`font-semibold ${SEV_COLORS[sev]}`}>{data.issues?.[sev] ?? 0}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
